/**
 * Overlapping optimistic saves of ONE unit (ISS-498 round 2, MED 5).
 *
 * Saves of a unit queue (mutation scope) but each applies its optimistic patch at click time, so
 * several can be pending at once. A plain "restore my own before-snapshot" rollback then misbehaves:
 *   - save 1 failing wipes save 2's still-pending change;
 *   - both failing re-installs save 1's phantom values (save 2's snapshot already contained them);
 *   - save 1's revalidation flashes old data over save 2's pending change.
 *
 * Instead the cache row is always DERIVED: server-truth base + the patches still pending. A save
 * settling removes its patch (folding it into the base when it succeeded) and re-derives. The
 * revalidation is skipped until the LAST pending save of the unit settles.
 *
 * Known transient (round 3, N2): the base is captured at the FIRST pending save of the unit and
 * every re-derive writes base + patches over the list row and the detail. A refetch that lands
 * while saves are still pending (another invalidation of the equipment keys, another tab's change)
 * is therefore overwritten with the click-time base until the last save settles and its
 * revalidation refetches. It self-corrects at that point; nothing is written to the server from it.
 */
import type { QueryClient } from '@tanstack/react-query';
import { dataHubEquipmentKeys } from '../useDataHubEquipment';
import type { Equipment } from '../../types/equipment';
import type { UpdateGeneratorInput } from './useGeneratorMutations';

type Updates = UpdateGeneratorInput['updates'];

/**
 * Translate a callable-shaped update into the Equipment shape the list and detail caches hold,
 * so a save is visible at click time: `clientName` is `customerName` there, street/city/state/zip
 * live under `address`, `generatorType` is `equipmentType`, and kW is a number. Keys that already
 * match pass through unchanged. Returns a NEW object; neither argument is mutated.
 */
function toDate(value: string): Date | undefined {
  if (value.trim() === '') return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

export function toEquipmentPatch(
  current: Equipment,
  updates: UpdateGeneratorInput['updates'],
): Equipment {
  const { clientName, generatorType, street, city, state, zip, generatorKW, installDate, warrantyExpires, status, ...rest } = updates;
  const addressTouched = [street, city, state, zip].some((v) => v !== undefined);
  return {
    ...current,
    ...(rest as Partial<Equipment>),
    ...(clientName !== undefined ? { customerName: clientName } : {}),
    ...(generatorType !== undefined ? { equipmentType: generatorType as Equipment['equipmentType'] } : {}),
    // Date | Timestamp in the Equipment shape: a raw 'YYYY-MM-DD' string would format back to ''
    // in the edit form, and reopening the unit before the refetch would then save the date blank.
    ...(installDate !== undefined ? { installDate: toDate(installDate) } : {}),
    ...(warrantyExpires !== undefined ? { warrantyExpires: toDate(warrantyExpires) } : {}),
    // `rawStatus` is what the edit form seeds from; keep it in step with the display bucket.
    ...(status !== undefined ? { status: status as Equipment['status'], rawStatus: status } : {}),
    ...(generatorKW !== undefined
      ? { generatorKW: generatorKW.trim() !== '' && Number.isFinite(Number(generatorKW)) ? Number(generatorKW) : undefined }
      : {}),
    ...(addressTouched
      ? {
          address: {
            street: street ?? current.address?.street ?? '',
            city: city ?? current.address?.city ?? '',
            state: state ?? current.address?.state ?? '',
            zip: zip ?? current.address?.zip ?? '',
          },
        }
      : {}),
  };
}


interface PendingEntry {
  /** The unit as the server last confirmed it (list-cache row), absent if it was not cached. */
  base: Equipment | undefined;
  baseDetail: Equipment | undefined;
  patches: Array<{ token: symbol; updates: Updates }>;
}

const pending = new Map<string, PendingEntry>();

function foldRow(base: Equipment | undefined, patches: PendingEntry['patches']): Equipment | undefined {
  return base ? patches.reduce((row, p) => toEquipmentPatch(row, p.updates), base) : undefined;
}

function write(queryClient: QueryClient, generatorId: string, entry: PendingEntry, patches: PendingEntry['patches']): void {
  const row = foldRow(entry.base, patches);
  if (row) {
    queryClient.setQueryData<Equipment[]>(dataHubEquipmentKeys.list(), (old) =>
      old?.map((eq) => (eq.equipmentId === generatorId ? row : eq)),
    );
  }
  const detail = foldRow(entry.baseDetail, patches);
  if (detail) queryClient.setQueryData<Equipment>(dataHubEquipmentKeys.detail(generatorId), detail);
}

/** Record a pending patch and show it. Returns the token the settle call needs. */
export function beginGeneratorPatch(queryClient: QueryClient, generatorId: string, updates: Updates): symbol {
  const token = Symbol(generatorId);
  let entry = pending.get(generatorId);
  if (!entry) {
    entry = {
      base: queryClient.getQueryData<Equipment[]>(dataHubEquipmentKeys.list())?.find((e) => e.equipmentId === generatorId),
      baseDetail: queryClient.getQueryData<Equipment>(dataHubEquipmentKeys.detail(generatorId)),
      patches: [],
    };
    pending.set(generatorId, entry);
  }
  entry.patches = [...entry.patches, { token, updates }];
  write(queryClient, generatorId, entry, entry.patches);
  return token;
}

/**
 * A save settled. Success folds its patch into the base; failure drops it. Re-derives the cache
 * from base + whatever is still pending. Returns true when it was the LAST pending save of the
 * unit (the caller revalidates only then).
 */
export function settleGeneratorPatch(
  queryClient: QueryClient,
  generatorId: string,
  token: symbol | undefined,
  succeeded: boolean,
): boolean {
  const entry = pending.get(generatorId);
  if (!entry || !token) return true;
  const mine = entry.patches.find((p) => p.token === token);
  const remaining = entry.patches.filter((p) => p.token !== token);
  if (succeeded && mine) {
    entry.base = foldRow(entry.base, [mine]);
    entry.baseDetail = foldRow(entry.baseDetail, [mine]);
  }
  entry.patches = remaining;
  write(queryClient, generatorId, entry, remaining);
  if (remaining.length === 0) {
    pending.delete(generatorId);
    return true;
  }
  return false;
}

/** Test-only: forget all pending patches (module state outlives a single test). */
export function resetPendingGeneratorPatchesForTests(): void {
  pending.clear();
}
